import { useCallback, useEffect, useState } from "react";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { adminPost } from "./adminApi";
import { consoleColors, primaryButtonSx } from "./consoleTheme";

function readImage(file) {
  return new Promise((resolve, reject) => {
    if (!file) return resolve("");
    if (file.size > 2000000) return reject(new Error("Use an image under 2 MB"));
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read the image"));
    reader.readAsDataURL(file);
  });
}

function splitDraw(value) {
  const text = String(value || "");
  const cut = text.indexOf(":");
  if (cut < 1) return { kind: "coupon", target_id: "" };
  return { kind: text.slice(0, cut) === "ticket" ? "ticket" : "coupon", target_id: text.slice(cut + 1) };
}

export default function LaunchImages() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    const result = await adminPost("LAUNCH_IMAGE_LIST");
    if (result.status === "200") setRows(result.data?.images || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (row) => {
    const result = await adminPost("LAUNCH_IMAGE_DELETE", { image_id: row.image_id });
    if (result.status === "200") {
      toast.success(result.data?.message || "Removed");
      load();
    } else toast.info(result.status_message || result.data?.error || "Could not remove");
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
        <Typography sx={{ color: consoleColors.muted, fontSize: 14 }}>These images appear on the left or right of the launch screen for the selected draw.</Typography>
        <Button variant="contained" sx={primaryButtonSx} onClick={() => navigate("/console/launch-images/new")}><Add sx={{ mr: 0.5 }} /> Add new</Button>
      </Box>
      {rows.length === 0 && (
        <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, p: 4, textAlign: "center", color: consoleColors.muted }}>
          No launch images yet.
        </Box>
      )}
      <Box sx={{ display: "grid", gap: 1.5 }}>
        {rows.map((row) => (
          <Box key={row.image_id} sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{row.name}</Typography>
              <Typography sx={{ fontSize: 13, color: consoleColors.muted, mt: 0.4 }}>{row.draw_label || "No draw"} · {row.side === "right" ? "Right" : "Left"}</Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/console/launch-images/${row.image_id}/edit`)}>Edit</Button>
              <Button size="small" sx={{ textTransform: "none" }} onClick={() => remove(row)}>Delete</Button>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export function LaunchImageForm() {
  const { imageId } = useParams();
  const navigate = useNavigate();
  const [draws, setDraws] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    image_id: "",
    name: "",
    image: "",
    draw_value: "",
    side: "left",
  });

  useEffect(() => {
    let mounted = true;
    const load = imageId
      ? adminPost("LAUNCH_IMAGE_DETAIL", { image_id: imageId })
      : adminPost("LAUNCH_IMAGE_LIST");
    load.then((result) => {
      if (!mounted || result.status !== "200") return;
      setDraws(result.data?.draws || []);
      const row = result.data?.image;
      if (row) {
        setForm({
          image_id: row.image_id,
          name: row.name || "",
          image: row.image || "",
          draw_value: row.draw_value || "",
          side: row.side === "right" ? "right" : "left",
        });
      }
    });
    return () => { mounted = false; };
  }, [imageId]);

  const onFile = async (file) => {
    try {
      const image = await readImage(file);
      if (image) setForm((current) => ({ ...current, image }));
    } catch (error) {
      toast.info(error.message || "Could not read the image");
    }
  };

  const save = async () => {
    const picked = splitDraw(form.draw_value);
    setSaving(true);
    try {
      const result = await adminPost("LAUNCH_IMAGE_SAVE", {
        image_id: form.image_id,
        name: form.name,
        image: form.image,
        kind: picked.kind,
        target_id: picked.target_id,
        side: form.side,
      });
      if (result.status === "200") {
        toast.success(result.data?.message || "Saved");
        navigate("/console/launch-images");
      } else toast.info(result.status_message || result.data?.error || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 720 }}>
      <Button sx={{ textTransform: "none", mb: 1.5 }} onClick={() => navigate("/console/launch-images")}>Back to Launch images</Button>
      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: { xs: 2, md: 3 }, display: "grid", gap: 1.6 }}>
        <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} helperText="A name for this launch image" />
        <Button variant="outlined" component="label" sx={{ textTransform: "none", justifyContent: "flex-start" }}>
          Image {form.image ? "· selected" : ""}
          <input hidden type="file" accept="image/*" onChange={(e) => onFile(e.target.files?.[0])} />
        </Button>
        {form.image && <Box component="img" src={form.image} alt="" sx={{ maxHeight: 220, width: "auto", maxWidth: "100%", objectFit: "contain", borderRadius: 1, border: `1px solid ${consoleColors.line}` }} />}
        <TextField select label="Draw" value={form.draw_value} onChange={(e) => setForm({ ...form, draw_value: e.target.value })} helperText="Shown when this draw is launched">
          <MenuItem value="">Select draw</MenuItem>
          {draws.map((draw) => (
            <MenuItem key={draw.value} value={draw.value}>{draw.label}</MenuItem>
          ))}
        </TextField>
        <TextField select label="Side" value={form.side} onChange={(e) => setForm({ ...form, side: e.target.value })} helperText="Left or right of the launch screen">
          <MenuItem value="left">Left</MenuItem>
          <MenuItem value="right">Right</MenuItem>
        </TextField>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" sx={primaryButtonSx} disabled={saving} onClick={save}>{saving ? "Saving..." : "Save"}</Button>
        </Box>
      </Box>
    </Box>
  );
}
